import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import './MultiSelect.css';

// Checklist dropdown — same visual language as Select.jsx, but for picking
// several options at once via checkboxes instead of one via radio-style
// selection. `options` is [{ value, label, meta? }]; `value` is an array of
// selected values.
const MultiSelect = ({
    value = [],
    onChange,
    options = [],
    placeholder = 'Select',
    searchPlaceholder = 'Search...',
    disabled = false,
    className = '',
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const rootRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = e => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
        const onKey = e => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    useEffect(() => { if (!open) setQuery(''); }, [open]);

    const filtered = query.trim()
        ? options.filter(o => o.label.toLowerCase().includes(query.trim().toLowerCase()))
        : options;

    const toggle = optValue => {
        onChange(value.includes(optValue) ? value.filter(v => v !== optValue) : [...value, optValue]);
    };

    const selectedLabels = options.filter(o => value.includes(o.value)).map(o => o.label);
    const summary = selectedLabels.length === 0
        ? placeholder
        : selectedLabels.length <= 2
            ? selectedLabels.join(', ')
            : `${selectedLabels.length} selected`;

    return (
        <div className={`ui-multiselect ${disabled ? 'is-disabled' : ''} ${className}`} ref={rootRef}>
            <button
                type="button"
                className={`ui-multiselect-trigger ${open ? 'is-open' : ''}`}
                onClick={() => !disabled && setOpen(v => !v)}
                disabled={disabled}
            >
                <span className={value.length ? 'ui-multiselect-value' : 'ui-multiselect-placeholder'}>{summary}</span>
                <ChevronDown size={15} className="ui-multiselect-chevron" />
            </button>

            {value.length > 0 && (
                <div className="ui-multiselect-chips">
                    {selectedLabels.slice(0, 6).map((label, i) => (
                        <span className="ui-multiselect-chip" key={value[i]}>
                            {label}
                            <button type="button" onClick={() => toggle(value[i])} aria-label={`Remove ${label}`}>
                                <X size={11} />
                            </button>
                        </span>
                    ))}
                    {selectedLabels.length > 6 && <span className="ui-multiselect-chip-more">+{selectedLabels.length - 6} more</span>}
                </div>
            )}

            {open && (
                <div className="ui-multiselect-menu" role="listbox">
                    <div className="ui-multiselect-search">
                        <Search size={13} />
                        <input
                            autoFocus
                            placeholder={searchPlaceholder}
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                        />
                    </div>
                    <div className="ui-multiselect-options">
                        {filtered.map(o => {
                            const checked = value.includes(o.value);
                            return (
                                <button
                                    type="button"
                                    key={o.value}
                                    className={`ui-multiselect-option ${checked ? 'is-checked' : ''}`}
                                    onClick={() => toggle(o.value)}
                                >
                                    <span className="ui-multiselect-checkbox">{checked && <Check size={12} />}</span>
                                    <span className="ui-multiselect-option-label">{o.label}</span>
                                    {o.meta && <span className="ui-multiselect-option-meta">{o.meta}</span>}
                                </button>
                            );
                        })}
                        {filtered.length === 0 && <div className="ui-multiselect-empty">No matches</div>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiSelect;
